<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("usage.title") }}
          </h6>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row align-items-end">
            <div class="col-md-3">
              <base-input :label="$t('usage.date_from')">
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  :config="{ allowInput: true, dateFormat: 'Y-m-d' }"
                  class="form-control datepicker"
                  v-model="dateFrom"
                ></flat-picker>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input :label="$t('usage.date_to')">
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  :config="{ allowInput: true, dateFormat: 'Y-m-d' }"
                  class="form-control datepicker"
                  v-model="dateTo"
                ></flat-picker>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input
                v-model="searchQuery"
                :placeholder="$t('search')"
                prepend-icon="fas fa-search"
              ></base-input>
            </div>
            <div class="col-md-3 d-flex form-group" style="gap:8px;">
              <base-button type="primary" class="usage-action-btn" @click="loadData()">
                <i class="fa fa-search"></i> {{ $t("search") }}
              </base-button>
              <base-button
                type="success"
                class="usage-action-btn"
                :disabled="!filteredData.length"
                @click="exportExcel()"
              >
                <i class="fas fa-file-excel"></i> {{ $t("usage.export_excel") }}
              </base-button>
            </div>
          </div>
          <div class="row mt-2" v-if="meta.date_from">
            <div class="col-md-12 text-muted small">
              {{ $t('usage.window') }}: {{ meta.date_from }} → {{ meta.date_to }}
              ({{ meta.days }} {{ $t('usage.days') }}) ·
              {{ $t('usage.total_orders') }}: {{ meta.total_orders }}
            </div>
          </div>
        </template>

        <el-table
          :data="filteredData"
          row-key="id"
          header-row-class-name="thead-light"
          v-loading="loading"
          :default-sort="{prop: 'usage', order: 'descending'}"
        >
          <el-table-column min-width="80" align="center">
            <template slot-scope="{ row }">
              <img
                v-if="row.image"
                :src="getImageUrl(row.image)"
                height="50px"
                style="max-width: 60px; object-fit: contain;"
              />
            </template>
          </el-table-column>

          <el-table-column :label="$t('usage.product_name')" prop="name" min-width="200" sortable></el-table-column>
          <el-table-column :label="$t('usage.barcode')" prop="barcode" min-width="140"></el-table-column>
          <el-table-column :label="$t('usage.usage_quantity')" prop="usage" min-width="120" align="right" sortable>
            <template slot-scope="{row}"><b>{{ (row.usage || 0).toLocaleString() }}</b></template>
          </el-table-column>
          <el-table-column :label="$t('usage.order_count')" prop="order_count" min-width="120" align="right" sortable>
            <template slot-scope="{row}">{{ (row.order_count || 0).toLocaleString() }}</template>
          </el-table-column>
        </el-table>

        <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
          <p class="card-category">
            {{ $t("data") }} {{ filteredData.length }} {{ $t("of") }} {{ usageData.length }}
          </p>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { Table, TableColumn } from "element-ui";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import swal from "sweetalert2";
import * as XLSX from "xlsx";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export default {
  components: {
    flatPicker,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  data() {
    // Default to a 7-day window ending today.
    const today = new Date();
    const week = new Date(today.getTime() - 6 * MS_PER_DAY);
    return {
      loading: false,
      dateFrom: this.fmtYMD(week),
      dateTo: this.fmtYMD(today),
      searchQuery: "",
      usageData: [],
      meta: { date_from: "", date_to: "", days: 0, total_orders: 0 },
    };
  },
  computed: {
    filteredData() {
      if (!this.searchQuery) return this.usageData;
      const q = this.searchQuery.toLowerCase();
      return this.usageData.filter((r) =>
        (r.name && r.name.toLowerCase().includes(q)) ||
        (r.barcode && r.barcode.toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions({
      listUsage: "WmsProduct/listUsage",
    }),
    fmtYMD(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    daysBetween(fromStr, toStr) {
      const from = new Date(fromStr + "T00:00:00");
      const to = new Date(toStr + "T00:00:00");
      if (isNaN(from.getTime()) || isNaN(to.getTime())) return -1;
      return Math.floor((to - from) / MS_PER_DAY) + 1;
    },
    validateRange() {
      if (!this.dateFrom || !this.dateTo) {
        swal.fire({
          title: this.$t("usage.error_required") || "Date range is required",
          icon: "warning",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-warning btn-fill",
        });
        return false;
      }
      const days = this.daysBetween(this.dateFrom, this.dateTo);
      if (days < 1) {
        swal.fire({
          title: this.$t("usage.error_order") || "date_to must be on or after date_from",
          icon: "warning",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-warning btn-fill",
        });
        return false;
      }
      if (days > 31) {
        swal.fire({
          title: this.$t("usage.error_too_long") || "Date range must not exceed 31 days",
          icon: "warning",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-warning btn-fill",
        });
        return false;
      }
      return true;
    },
    async loadData() {
      if (!this.validateRange()) return;
      this.loading = true;
      // Blocking "querying…" modal — same pattern as PackingTables /
      // ReportCommission. Closed in the finally block whether the request
      // succeeds or throws.
      swal.fire({
        title: this.$t("usage.processing") || "Processing…",
        text: this.$t("usage.processing_hint") || "Please wait while orders are aggregated.",
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        onOpen: () => { swal.showLoading(); },
      });
      try {
        const resp = await this.listUsage({
          date_from: this.dateFrom,
          date_to: this.dateTo,
        });
        // Backend returns { date_from, date_to, days, total_orders, items }
        // wrapped in r.Set(...) → response.data.Datum.
        const datum = (resp.data && resp.data.Datum) || {};
        this.usageData = Array.isArray(datum.items) ? datum.items : [];
        this.meta = {
          date_from: datum.date_from || "",
          date_to: datum.date_to || "",
          days: datum.days || 0,
          total_orders: datum.total_orders || 0,
        };
        swal.close();
      } catch (e) {
        console.log(e);
        const msg = (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) || "Failed";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
      this.loading = false;
    },
    getImageUrl(image) {
      if (!image) return "";
      if (image.startsWith("http")) {
        const apiBase = process.env.VUE_APP_API_BASE_URL || "";
        if (apiBase && image.startsWith(apiBase)) return image;
        return "";
      }
      const safe = image.replace(/\.\./g, "");
      if (!safe) return "";
      return process.env.VUE_APP_API_BASE_URL + "/files/wms_products/" + safe;
    },
    exportExcel() {
      const headers = [
        this.$t("usage.product_name"),
        this.$t("usage.barcode"),
        this.$t("usage.usage_quantity"),
        this.$t("usage.order_count"),
      ];
      const rows = this.filteredData.map((r) => [
        r.name,
        r.barcode,
        r.usage,
        r.order_count,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      const sheetName = `${this.meta.date_from || this.dateFrom}_${this.meta.date_to || this.dateTo}`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
      XLSX.writeFile(wb, `stock_usage_${this.meta.date_from || this.dateFrom}_${this.meta.date_to || this.dateTo}.xlsx`);
    },
  },
};
</script>

<style scoped>
/* Match the .form-control height used by base-input / flatpickr so the
   action buttons line up cleanly with the inputs in the same row. */
.usage-action-btn {
  height: calc(2.75rem + 2px); /* same as Argon's .form-control */
  padding-top: 0;
  padding-bottom: 0;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
</style>
